import { normalizeTimezoneIdentifier } from "@repo/utils";

const resolveTimezoneIdentifier = (timeZone?: string | null): string => {
  const fallback = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!timeZone) return fallback;
  const normalized = normalizeTimezoneIdentifier(timeZone);
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: normalized }).format();
    return normalized;
  } catch {
    return fallback;
  }
};

const datePartsInTimeZone = (date: Date, timeZone?: string | null) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: resolveTimezoneIdentifier(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
};

export const todayIsoInTimeZone = (timeZone?: string | null): string => {
  const { year, month, day } = datePartsInTimeZone(new Date(), timeZone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const timeZoneOffsetMs = (date: Date, timeZone?: string | null) => {
  const { year, month, day, hour, minute, second } = datePartsInTimeZone(
    date,
    timeZone,
  );
  return (
    Date.UTC(year, month - 1, day, hour, minute, second) -
    Math.floor(date.getTime() / 1000) * 1000
  );
};

const localDateBoundaryUtcSeconds = (
  iso: string,
  timeZone: string | null | undefined,
  boundary: "start" | "end",
) => {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const isEnd = boundary === "end";
  const wallTimeUtc = Date.UTC(
    year,
    month - 1,
    day,
    isEnd ? 23 : 0,
    isEnd ? 59 : 0,
    isEnd ? 59 : 0,
    isEnd ? 999 : 0,
  );
  let utcTime = wallTimeUtc;
  for (let pass = 0; pass < 2; pass += 1)
    utcTime = wallTimeUtc - timeZoneOffsetMs(new Date(utcTime), timeZone);
  return Math.floor(utcTime / 1000);
};

export const localDateStartUtcSeconds = (
  iso: string,
  timeZone?: string | null,
) => localDateBoundaryUtcSeconds(iso, timeZone, "start");
export const localDateEndUtcSeconds = (iso: string, timeZone?: string | null) =>
  localDateBoundaryUtcSeconds(iso, timeZone, "end");
